import React, { useState, useEffect } from 'react';
import {
  BookOpen, ChevronRight, ChevronLeft, X, Lightbulb, Target, Shield,
  Terminal, Camera, Lock, Zap, Brain, Rocket, Play, CheckCircle,
  HelpCircle, Skull, AlertTriangle, Network, Eye, ArrowRight,
  Sparkles, MessageSquare, Bot, Server, Clock, Activity
} from 'lucide-react';

// Tutorial steps for Sessions page
const tutorialSteps = [
  {
    id: 'welcome',
    title: 'Bienvenue dans les Sessions d\'Exploitation',
    icon: Skull,
    content: `Cette page vous permet de gérer vos sessions d'accès aux systèmes compromis.

Une "session" représente une connexion active vers un système cible après une exploitation réussie. C'est votre point d'entrée pour les actions de post-exploitation.`,
    tips: [
      'Les sessions actives sont en vert',
      'Les sessions fermées sont en gris',
      'Chaque session peut être contrôlée indépendamment'
    ],
    highlight: 'stats'
  },
  {
    id: 'stats',
    title: 'Comprendre les Statistiques',
    icon: Activity,
    content: `Les cartes de statistiques vous donnent une vue d'ensemble:

- **Total Sessions**: Nombre total de sessions créées
- **Actives**: Sessions actuellement connectées
- **Fermées**: Sessions terminées ou déconnectées
- **Persistantes**: Sessions avec mécanisme de persistance
- **Privilégiées**: Sessions avec droits root/admin
- **Hôtes Compromis**: Nombre de machines uniques`,
    tips: [
      'Une session privilégiée a plus de capacités',
      'La persistance permet de revenir même après redémarrage'
    ],
    highlight: 'stats-cards'
  },
  {
    id: 'ai-assistant',
    title: 'Assistant IA - Votre Guide',
    icon: Brain,
    content: `L'Assistant IA vous aide à analyser et planifier vos actions:

1. Cliquez sur "Assistant IA" en haut à droite
2. Utilisez les boutons rapides ou posez vos questions
3. L'IA analysera vos sessions et donnera des recommandations

L'IA peut vous aider pour:
- Choisir les meilleures techniques de privesc
- Planifier les mouvements latéraux
- Recommander des actions de post-exploitation`,
    tips: [
      'Demandez "Quelles sessions dois-je privilégier?"',
      'Demandez "Comment escalader les privilèges sur Linux?"',
      'L\'IA connaît le contexte de vos sessions'
    ],
    highlight: 'ai-button'
  },
  {
    id: 'poc-auto',
    title: 'PoC Auto - Exploitation Simplifiée',
    icon: Rocket,
    content: `Le module PoC Auto vous permet d'exploiter automatiquement des vulnérabilités:

1. Cliquez sur "PoC Auto" pour ouvrir le panneau
2. Sélectionnez les vulnérabilités à exploiter
3. Cliquez sur "Exécuter PoC" pour lancer l'exploitation

Si l'exploitation réussit, une nouvelle session sera créée automatiquement.`,
    tips: [
      'Sélectionnez plusieurs vulnérabilités en une fois',
      'Les résultats apparaissent en temps réel',
      'Vous pouvez copier le code PoC généré'
    ],
    highlight: 'poc-button'
  },
  {
    id: 'session-actions',
    title: 'Actions sur les Sessions',
    icon: Zap,
    content: `Chaque session active offre plusieurs actions:

- **Screenshot**: Capture l'écran du système cible
- **Privilege Escalation**: Tente d'obtenir les droits root/admin
- **Persistence**: Installe un mécanisme pour revenir plus tard
- **Terminal**: Ouvre un shell interactif

Cliquez sur une session pour voir plus de détails et options.`,
    tips: [
      'Les icônes d\'action sont à droite de chaque session',
      'Les actions désactivées sont grisées',
      'ROOT = privilèges élevés, PERSISTENT = accès permanent'
    ],
    highlight: 'session-list'
  },
  {
    id: 'apt-modules',
    title: 'Modules APT Avancés',
    icon: Shield,
    content: `Les modules APT (Menace Persistante Avancée) simulent des techniques d'attaque sophistiquées:

- Chaque module correspond à des techniques MITRE ATT&CK
- Sélectionnez un module pour voir ses détails
- Exécutez-le sur vos sessions actives

Ces modules sont utiles pour tester la résilience d'un système.`,
    tips: [
      'Les techniques MITRE sont affichées en badges rouges',
      'Certains modules nécessitent des privilèges élevés',
      'Les résultats sont visibles dans l\'exécution'
    ],
    highlight: 'apt-modules'
  },
  {
    id: 'workflow',
    title: 'Workflow de Pénétration',
    icon: Network,
    content: `Le diagramme de workflow en bas de page montre le processus complet:

1. **Reconnaissance**: Collecte d'informations
2. **Scanning**: Détection de vulnérabilités
3. **Exploitation**: Création de sessions
4. **Post-Exploitation**: Actions sur les sessions
5. **Rapport**: Documentation des résultats

Vous êtes actuellement dans la phase "Post-Exploitation".`,
    tips: [
      'Suivez le workflow pour une méthodologie structurée',
      'Chaque phase a ses propres outils',
      'Le rapport final documente tout le processus'
    ],
    highlight: 'workflow'
  }
];

// Quick help topics
const quickHelpTopics = [
  {
    question: 'Comment créer une nouvelle session?',
    answer: 'Les sessions sont créées automatiquement lorsqu\'une vulnérabilité est exploitée avec succès. Utilisez le module "PoC Auto" ou lancez un scan avec exploitation activée.',
    icon: Terminal
  },
  {
    question: 'Que faire si une session est fermée?',
    answer: 'Une session fermée ne peut plus être utilisée. Si vous avez installé la persistence, vous pouvez tenter de vous reconnecter. Sinon, vous devrez ré-exploiter la vulnérabilité.',
    icon: AlertTriangle
  },
  {
    question: 'Comment escalader les privilèges?',
    answer: 'Cliquez sur l\'icône bouclier (Shield) à côté d\'une session active. Le système tentera automatiquement plusieurs techniques de privilege escalation.',
    icon: Shield
  },
  {
    question: 'À quoi sert la persistence?',
    answer: 'La persistence installe un mécanisme qui permet de revenir sur le système même après un redémarrage. C\'est utile pour maintenir l\'accès dans le temps.',
    icon: Lock
  },
  {
    question: 'Comment utiliser le terminal interactif?',
    answer: 'Cliquez sur l\'icône Terminal ou sur une session pour ouvrir le shell interactif. Vous pourrez exécuter des commandes directement sur le système cible.',
    icon: Terminal
  },
  {
    question: 'Que signifient les badges ROOT et PERSISTENT?',
    answer: 'ROOT indique que la session a des privilèges administrateur. PERSISTENT indique qu\'un mécanisme de persistence est installé pour maintenir l\'accès.',
    icon: Skull
  }
];

// Main Guide Component
const SessionsGuide = ({
  isOpen,
  onClose,
  onStartTutorial,
  showWelcome = false,
  onDismissWelcome
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showQuickHelp, setShowQuickHelp] = useState(false);
  const [expandedHelp, setExpandedHelp] = useState(null);

  // Tutorial navigation
  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  // Current step data
  const step = tutorialSteps[currentStep];
  const StepIcon = step?.icon || BookOpen;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-brand-black-light rounded-2xl border border-brand-green/30 w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-brand-green/20 bg-gradient-to-r from-brand-green/10 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Guide des Sessions</h2>
                <p className="text-gray-400 text-sm">Apprenez à utiliser les sessions d'exploitation</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowQuickHelp(!showQuickHelp)}
                className={`p-2 rounded-lg transition-colors ${
                  showQuickHelp
                    ? 'bg-brand-green text-brand-black'
                    : 'bg-brand-green/20 text-brand-green hover:bg-brand-green/30'
                }`}
                title="Aide rapide"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {showQuickHelp ? (
            /* Quick Help View */
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-brand-green" />
                Questions Fréquentes
              </h3>
              <div className="space-y-3">
                {quickHelpTopics.map((topic, idx) => {
                  const TopicIcon = topic.icon;
                  return (
                    <div
                      key={idx}
                      className="bg-brand-black rounded-xl border border-gray-700 overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedHelp(expandedHelp === idx ? null : idx)}
                        className="w-full p-4 flex items-center justify-between text-left hover:bg-brand-black-lighter transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <TopicIcon className="w-5 h-5 text-brand-green" />
                          <span className="text-white font-medium">{topic.question}</span>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedHelp === idx ? 'rotate-90' : ''
                        }`} />
                      </button>
                      {expandedHelp === idx && (
                        <div className="p-4 pt-0 border-t border-gray-700">
                          <p className="text-gray-300 text-sm leading-relaxed">{topic.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Tutorial View */
            <div className="space-y-6">
              {/* Step indicator */}
              <div className="flex items-center justify-center gap-2">
                {tutorialSteps.map((s, idx) => (
                  <button
                    key={s.id}
                    onClick={() => goToStep(idx)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx === currentStep
                        ? 'bg-brand-green w-6'
                        : idx < currentStep
                        ? 'bg-brand-green/50'
                        : 'bg-gray-600'
                    }`}
                    title={s.title}
                  />
                ))}
              </div>

              {/* Current step content */}
              <div className="bg-brand-black rounded-xl border border-gray-700 p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center flex-shrink-0">
                    <StepIcon className="w-7 h-7 text-brand-green" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-brand-green font-mono">
                        Étape {currentStep + 1}/{tutorialSteps.length}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white">{step.title}</h3>
                  </div>
                </div>

                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 whitespace-pre-line leading-relaxed">
                    {step.content}
                  </p>
                </div>

                {/* Tips */}
                {step.tips && step.tips.length > 0 && (
                  <div className="mt-6 bg-brand-green/10 rounded-lg p-4 border border-brand-green/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-5 h-5 text-brand-green" />
                      <span className="text-brand-green font-medium">Conseils</span>
                    </div>
                    <ul className="space-y-2">
                      {step.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                          <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>

                <span className="text-gray-400 text-sm">
                  {currentStep + 1} / {tutorialSteps.length}
                </span>

                {currentStep < tutorialSteps.length - 1 ? (
                  <button
                    onClick={nextStep}
                    className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 font-medium"
                  >
                    Suivant
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 font-medium"
                  >
                    Terminer
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer with AI suggestion */}
        <div className="p-4 border-t border-gray-700 bg-brand-black/50">
          <div className="flex items-center gap-3 text-sm">
            <Bot className="w-5 h-5 text-blue-400" />
            <span className="text-gray-400">
              Besoin d'aide personnalisée? Utilisez l'
              <button
                onClick={() => {
                  onClose();
                  // This will be handled by parent component
                }}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Assistant IA
              </button>
              {' '}pour des conseils adaptés à votre situation.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Welcome Banner Component
export const SessionsWelcomeBanner = ({ onStartTutorial, onDismiss }) => {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user has dismissed before
    const hasDismissed = localStorage.getItem('sessions_welcome_dismissed');
    if (hasDismissed) {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('sessions_welcome_dismissed', 'true');
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  if (dismissed) return null;

  return (
    <div className="bg-gradient-to-r from-brand-green/20 via-blue-500/10 to-purple-500/10 rounded-xl border border-brand-green/30 p-6 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-7 h-7 text-brand-green" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-2">
              Bienvenue dans les Sessions d'Exploitation!
            </h3>
            <p className="text-gray-300 mb-4 max-w-2xl">
              Cette page vous permet de gérer vos accès aux systèmes compromis.
              Découvrez comment utiliser les sessions, l'Assistant IA, et les modules
              d'exploitation automatique pour optimiser votre pentest.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onStartTutorial}
                className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 font-medium"
              >
                <BookOpen className="w-4 h-4" />
                Suivre le tutoriel
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
              >
                J'ai compris
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Quick start hints */}
      <div className="mt-6 pt-6 border-t border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <Brain className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h4 className="text-white font-medium text-sm">Assistant IA</h4>
            <p className="text-gray-400 text-xs">Posez vos questions sur l'exploitation</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-purple-500/20">
            <Rocket className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h4 className="text-white font-medium text-sm">PoC Auto</h4>
            <p className="text-gray-400 text-xs">Exploitez les vulnérabilités en 1 clic</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-orange-500/20">
            <Shield className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h4 className="text-white font-medium text-sm">Actions Rapides</h4>
            <p className="text-gray-400 text-xs">Privesc, persistence, screenshots</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Floating Help Button
export const SessionsHelpButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 w-14 h-14 bg-brand-green text-brand-black rounded-full shadow-lg hover:bg-brand-green/90 transition-all hover:scale-110 flex items-center justify-center z-40 group"
      title="Aide et tutoriel"
    >
      <HelpCircle className="w-6 h-6" />
      <span className="absolute right-full mr-3 bg-brand-black-light text-white px-3 py-1.5 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-gray-700">
        Besoin d'aide?
      </span>
    </button>
  );
};

// Contextual Tooltip Component
export const ContextualTooltip = ({ id, title, content, position = 'bottom' }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: 'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 -translate-y-1/2',
  };

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="p-1 text-gray-500 hover:text-brand-green transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {isVisible && (
        <div className={`absolute z-50 ${positionClasses[position]} w-64`}>
          <div className="bg-brand-black-light border border-brand-green/30 rounded-lg p-3 shadow-xl">
            {title && (
              <h4 className="text-brand-green font-medium text-sm mb-1">{title}</h4>
            )}
            <p className="text-gray-300 text-xs">{content}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsGuide;
